import React, {useState} from 'react'
import gql from 'graphql-tag'
import {notification} from 'antd'
import {Mutation} from 'react-apollo'
import {Redirect} from 'react-router-dom'
import Heading from 'components/shared/Heading'
import Query from 'components/shared/Query'
import QueryParams from 'components/shared/QueryParams'
import Price from 'components/shared/Price'
import Button from 'components/shared/Button'
import './index.scss'

const productFragment = gql`
  fragment ProductFragment on Product {
    id
    name
    state
    createdAt
    ...on PhotoBasedProduct {
      quantity
    }
    ...on AssetsBasedProduct {
      remainingCredits
      extraAssetUnitPrice
      assets {
        edges {
          node {
            id
            state
            ...on PhotoAsset {
              image(size: "monitor") {
                url
                thumbUrl
                width
                height
                labels
                filenameIndex
              }
            }
            ...on PlanDrawingAsset {
              free
            }
          }
        }
      }
    }
    ...on FloorPlansProduct {
      quantity
    }
  }
`

const productQuery = gql`
  query ApproveProductQuery($id: ID!) {
    product(id: $id) {
      ...ProductFragment
    }
  }
  ${productFragment}
`

const purchaseAssetsMutation = gql`
  mutation PurchaseAssets($input: PurchaseAssetsInput!) {
    PurchaseAssets(input: $input) {
      product {
        ...ProductFragment
      }
    }
  }
  ${productFragment}
`

const assetTerms = {
  default: {
    one: 'bilde',
    other: 'bilder',
  },
  FloorPlansProduct: {
    one: 'plantegning',
    other: 'plantegninger',
  },
}

const ProductWithAssets = ({match, product, assets}) => {
  const [redirectToProduct, setRedirectToProduct] = useState(false)

  if (redirectToProduct) {
    return <Redirect to={`/oppdrag/${match.params.estateId}/produkt/${product.id}`} />
  }

  const purchasedAssets = product.assets.edges.map(o => o.node).filter(a => a.state === 'purchased')
  const additionalCost = (assets.length - product.remainingCredits) * product.extraAssetUnitPrice
  const showTooFewAssetsWarning = (assets.length + purchasedAssets.length) < product.quantity

  const assetTerm = assetTerms[product.__typename] || assetTerms.default
  const pluralizedAssetTerm = assetTerm[assets.length === 1 ? 'one' : 'other']

  return (
    <div className="approve-product">
      <div className="photos">
        <span>Du har valgt totalt</span>
        <span className="count">{assets.length}</span>
        <span>{pluralizedAssetTerm}</span>
      </div>

      {showTooFewAssetsWarning && (
        <p>Du har valgt færre {assetTerm.other} enn bestilt!</p>
      )}

      {additionalCost > 0 && (
        <p>Ekstra kostnad kr <Price price={additionalCost} /> legges til ordre.</p>
      )}

      <div className="actions">
        <Mutation
          mutation={purchaseAssetsMutation}
          onCompleted={() => {
            notification['success']({
              message: 'Leveranse godkjent',
              description: 'Leveransen ble godkjent og vil snart bli tilgjengelig på produktsiden.',
            })
            setRedirectToProduct(true)
          }}
          onError={() => {
            notification['error']({
              message: 'Godkjenning feilet',
              description: 'Kunne ikke godkjenne leveransen grunnet en feil. Forsøk på nytt senere.',
            })
          }}
        >
          {(purchaseAssets, {loading}) => {
            return (
              <Button
                type="action"
                loading={loading}
                disabled={loading}
                onClick={() => {
                  purchaseAssets({
                    variables: {
                      input: {
                        productId: product.id,
                        assetIds: assets.map(a => a.id),
                      }
                    }
                  })
                }}
              >
                Godkjenn
              </Button>
            )
          }}
        </Mutation>
      </div>

      <p>Leveransen blir tilgjengelig i meglersystemet ditt innen en halvtime.</p>
    </div>
  )
}

const ApproveProduct = ({match}) => {
  return (
    <QueryParams>
      {({queryParams}) => {
        return (
          <>
            <Heading level={2}>Godkjenn og last ned</Heading>
            <Query
              inlineLoading
              inlineError
              query={productQuery}
              variables={{id: match.params.productId}}
            >
              {({getData}) => {
                const product = getData('product')
                const allAssets = product.assets.edges.map(e => e.node).filter(a => a.state === 'available_for_purchase')
                // Sometimes assetIds is an array and other times an object keyed by an index. But
                // you can use Object.values() on either and get the same result
                const assets = queryParams.assetIds
                  ? allAssets.filter(a => Object.values(queryParams.assetIds).includes(a.id))
                  : allAssets

                return (
                  <ProductWithAssets
                    match={match}
                    product={product}
                    assets={assets}
                  />
                )
              }}
            </Query>
          </>
        )
      }}
    </QueryParams>
  )
}

export default ApproveProduct
